import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Ban, BadgeCheck, IdCard, MessagesSquare, ShieldCheck, Tags } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import {
  fetchGroups,
  banGroup,
  unbanGroup,
  verifyGroup,
  labelGroup,
  clearGroupLabel,
  type AdminGroupRow,
  type ModerationLabel,
} from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { DangerDialog } from '@/components/DangerDialog'
import { LabelDialog } from '@/components/LabelDialog'
import { useToast } from '@/providers/UiProviders'

/**
 * A4.01 — список груп/каналів (модерація). GET /admin/groups.
 * Бан/верифікація — реальні дії через EventFlow command bus у AdminServer.
 */
export function GroupsScreen() {
  const t = useT()
  const { toast } = useToast()
  const navigate = useNavigate()
  const [reloadKey, setReloadKey] = useState(0)
  const [banTarget, setBanTarget] = useState<AdminGroupRow | null>(null)
  const [labelTarget, setLabelTarget] = useState<AdminGroupRow | null>(null)

  const run = useCallback(
    async (fn: () => Promise<unknown>) => {
      try {
        await fn()
        toast(t.users.actionDone, { tone: 'success' })
        setReloadKey((k) => k + 1)
      } catch (e) {
        toast(t.users.actionFailed, {
          tone: 'error',
          ...(e instanceof Error && e.message ? { description: e.message } : {}),
        })
      }
    },
    [toast, t],
  )

  const rowActions = useMemo<RowAction<AdminGroupRow>[]>(
    () => [
      {
        id: 'card',
        label: t.users.viewCard,
        icon: <IdCard size={13} />,
        onSelect: (r) => navigate(`/groups/${r.id}`),
      },
      {
        id: 'ban',
        label: t.users.ban,
        icon: <Ban size={13} />,
        danger: true,
        permission: PERMISSIONS.USERS_MODERATE,
        visible: (r) => !r.is_banned,
        onSelect: (r) => setBanTarget(r),
      },
      {
        id: 'unban',
        label: t.users.unban,
        icon: <ShieldCheck size={13} />,
        permission: PERMISSIONS.USERS_MODERATE,
        visible: (r) => r.is_banned,
        onSelect: (r) => void run(() => unbanGroup(r.id)),
      },
      {
        id: 'verify',
        label: t.users.verify,
        icon: <BadgeCheck size={13} />,
        permission: PERMISSIONS.USERS_MODERATE,
        visible: (r) => !r.verified,
        onSelect: (r) => void run(() => verifyGroup(r.id, true)),
      },
      {
        id: 'unverify',
        label: t.users.unverify,
        icon: <BadgeCheck size={13} />,
        permission: PERMISSIONS.USERS_MODERATE,
        visible: (r) => r.verified,
        onSelect: (r) => void run(() => verifyGroup(r.id, false)),
      },
      {
        id: 'label',
        label: t.users.label,
        icon: <Tags size={13} />,
        permission: PERMISSIONS.USERS_MODERATE,
        onSelect: (r) => setLabelTarget(r),
      },
    ],
    [t, run, navigate],
  )

  const columns = useMemo<AdminColumn<AdminGroupRow>[]>(
    () => [
      {
        key: 'type',
        header: t.col.type,
        type: 'enum',
        width: 110,
        options: [
          { value: 'group', label: t.groups.group, status: 'active' },
          { value: 'channel', label: t.groups.channel, status: 'review' },
        ],
      },
      { key: 'title', header: t.col.title, type: 'text', minWidth: 200 },
      { key: 'username', header: t.col.username, type: 'text', width: 160 },
      { key: 'member_count', header: t.groups.members, type: 'number', width: 110 },
      { key: 'is_forum', header: t.groups.forum, type: 'bool', width: 90 },
      { key: 'verified', header: t.users.verify, type: 'bool', width: 90 },
      { key: 'is_banned', header: t.status.banned, type: 'bool', width: 90 },
      { key: 'label', header: t.users.label, type: 'text', width: 100, sortable: false },
      { key: 'created_at', header: t.col.registered, type: 'date', width: 150 },
    ],
    [t],
  )

  const fetcher = useCallback(fetchGroups, [])

  return (
    <>
      <PageHeader title={t.nav.groups} screenId="A4.01" verified />
      <DataTable<AdminGroupRow>
        key={reloadKey}
        screenId="A4.01"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <MessagesSquare size={28} strokeWidth={1.5} /> }}
      />

      {banTarget && (
        <DangerDialog
          open
          onClose={() => setBanTarget(null)}
          screenId="A4.27"
          level={2}
          title={t.users.banTitle(banTarget.title || `#${banTarget.id}`)}
          description={t.users.banDesc}
          confirmLabel={t.users.ban}
          onConfirm={async ({ reason }) => {
            try {
              await banGroup(banTarget.id, reason)
              toast(t.users.actionDone, { tone: 'success' })
              setReloadKey((k) => k + 1)
            } catch (e) {
              toast(t.users.actionFailed, {
                tone: 'error',
                ...(e instanceof Error && e.message ? { description: e.message } : {}),
              })
              throw e
            }
          }}
        />
      )}

      {labelTarget && (
        <LabelDialog
          open
          onClose={() => setLabelTarget(null)}
          title={t.users.labelTitle(labelTarget.title || `#${labelTarget.id}`)}
          currentLabel={labelTarget.label}
          onSelect={(label: ModerationLabel) => run(() => labelGroup(labelTarget.id, label))}
          onClear={() => run(() => clearGroupLabel(labelTarget.id))}
        />
      )}
    </>
  )
}
